import type { CSSProperties } from 'react'
import { Skeleton } from 'tailwind-v4/components/skeleton'

/**
 * Loading skeleton shaped like the day planner — header bar, left
 * bookings-list panel, resource column, time axis and a grid of rows
 * with pill placeholders — so the wait reads as "the planner is coming"
 * rather than a generic card page.
 *
 * Deliberately dependency-free beyond the v4 Skeleton: it is ALSO used
 * as the route-level Suspense fallback (imported statically by
 * paths.tsx), so it must not drag the planner chunk into the main
 * bundle.
 *
 * Fills the whole viewport: the left panel's card list and the grid's
 * rows both `flex-1` + `overflow-hidden`, and we render more of each
 * than any realistic screen shows so the surface is full top-to-bottom
 * (extra rows/cards are clipped, never leaving blank space below).
 *
 * Geometry mirrors the real planner (index.tsx): h-14 header · w-80 left
 * panel · w-32 resource column · h-11 axis · 24px (h-6) rows ·
 * rounded-[6px] pills · faint vertical hour lines + a NOW marker.
 */

// Deterministic left-panel booking cards (pre-formatted CSS widths for
// the name / meta lines). Hand-derived so it reads like a believable
// service rather than random noise.
const PANEL_CARDS = Array.from({ length: 12 }, (_, i) => ({
  id: `card-${String(i)}`,
  name: `${String(46 + ((i * 13) % 42))}%`, // 46–88%
  meta: `${String(30 + ((i * 7) % 34))}%`, // 30–64%
}))

// Time-axis slot ticks.
const AXIS_TICKS = Array.from({ length: 8 }, (_, i) => `tick-${String(i)}`)

// Deterministic grid rows: ~60% carry a pill (pre-formatted left / width);
// the rest are empty. Enough rows to overflow a tall screen (clipped by
// the container) so the grid is always full-height.
const GRID_ROWS = Array.from({ length: 40 }, (_, i) => {
  const hasPill = (i * 7 + 3) % 5 < 3
  const pill = hasPill
    ? {
        left: `${String((i * 17 + 6) % 55)}%`,
        width: `${String(14 + ((i * 11) % 5) * 4)}%`,
      }
    : null
  return { id: `row-${String(i)}`, pill }
})

// Faint vertical hour lines across the grid pane (starts after the w-32
// resource column). Token-driven, drawn as a repeating gradient so it
// costs no extra DOM.
const HOUR_LINES: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(to right, var(--border) 0, var(--border) 1px, transparent 1px, transparent 12.5%)',
}

// NOW marker sits ~58% across the grid pane (after the resource column).
const NOW_MARKER: CSSProperties = { left: 'calc(32px + (100% - 32px) * 0.58)' }

export function DayPlannerSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading day planner"
      className="flex h-full min-h-0 flex-col bg-background"
    >
      {/* Header — date group left, action cluster + Create right */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <Skeleton className="h-9 w-40 rounded-lg md:w-72" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="hidden size-8 rounded-lg md:block" />
          <Skeleton className="hidden size-8 rounded-lg md:block" />
          <Skeleton className="hidden size-8 rounded-lg md:block" />
          <Skeleton className="size-8 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Left bookings-list panel — desktop only, fills its height */}
        <div className="hidden w-80 shrink-0 flex-col border-r border-border md:flex">
          <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-3">
            <Skeleton className="h-4 w-28 rounded" />
            <Skeleton className="size-6 rounded-md" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2">
            {PANEL_CARDS.map((card) => (
              <div
                key={card.id}
                className="flex shrink-0 items-start gap-3 rounded-lg border border-border/60 p-2.5"
              >
                <Skeleton className="h-9 w-11 shrink-0 rounded-md" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
                  <Skeleton
                    className="h-3 rounded"
                    style={{ width: card.name }}
                  />
                  <Skeleton
                    className="h-2.5 rounded"
                    style={{ width: card.meta }}
                  />
                </div>
                <Skeleton className="h-5 w-6 shrink-0 rounded-full" />
              </div>
            ))}
          </div>
        </div>

        {/* Grid — resource column + axis + rows, fills its height */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Time axis band (h-11): corner cell + slot ticks */}
          <div className="flex h-11 shrink-0 items-center border-b border-border">
            <div className="flex h-full w-32 shrink-0 items-center border-r border-border px-2.5">
              <Skeleton className="h-3 w-14 rounded" />
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-between overflow-hidden px-4">
              {AXIS_TICKS.map((tick) => (
                <Skeleton key={tick} className="h-3 w-6 shrink-0 rounded" />
              ))}
            </div>
          </div>

          {/* Rows — fill remaining height; faint hour lines + NOW marker */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 left-32"
              style={HOUR_LINES}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 w-px bg-foreground/25"
              style={NOW_MARKER}
            />
            {GRID_ROWS.map((row) => (
              <div key={row.id} className="flex h-6 border-b border-border/60">
                <div className="flex w-32 shrink-0 items-center gap-2 border-r border-border px-2.5">
                  <Skeleton className="size-2 shrink-0 rounded-full" />
                  <Skeleton className="h-2.5 w-12 rounded" />
                </div>
                <div className="relative min-w-0 flex-1">
                  {row.pill ? (
                    <Skeleton
                      className="absolute top-px h-[22px] rounded-[6px]"
                      style={{ left: row.pill.left, width: row.pill.width }}
                    />
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DayPlannerSkeleton
